// ── ladderRows — Compact value-ladder collapse for GameLinesSection ──
//
// A value LADDER is a set of rows differing ONLY by a numeric threshold: a
// player+stat's 1+/2+/3+ Hits, a team's runs ladder. Those are points on a
// scale — the same relationship a total's 8.5 and 9.5 have — so Compact
// collapses each ladder into ONE row carrying the shared MiniLineStrip, and
// you scroll thresholds in place. That is the compact Spread/Total model, and
// props/events now follow it exactly (Dan, 2026-07-24).
//
// It stays the section's NORMAL table, so every analytics column (Odds / K Vol
// / K Liq / K Q / K Δm and the Poly four) survives the density switch, the way
// they do on compact Spread/Total. The rungs previously collapsed into
// CompactOutcomeBands (#141), which drops those columns by design — right for
// Moneyline / BTTS, which are genuinely distinct outcomes with no scale, wrong
// for a ladder. Bands keep the outcome tabs; ladders come here.
//
// Kept wholesale from #141 (Patricia), because they are layout-independent and
// were the right calls:
//   • selection stored by rung IDENTITY (market ticker), never array index, so
//     hiding or settling a LOWER rung can't slide the selection to a different
//     threshold;
//   • a lone rung is not a ladder — full "N+" label, no strip;
//   • the grouping key excludes the threshold, so different players / stats /
//     teams / venues never collapse together.
//
// Pure — no store, hooks or React.

import { closestKalshiYesTo50 } from './CompactLinesTable';
import type { LineChipItem } from '../SpreadsTotals/MiniLineStrip';
import type { GameRow, TeamSide, VenueBook } from '../ValueDashboard/types';

/** A rung's stable chip id = its real market ticker (Kalshi preferred, else
 *  Poly), falling back to the row key only if a rung had no book (can't happen
 *  in practice — bookless rungs are skipped). Shared by the row builder and the
 *  deep-link resolver so both agree on the id a selection is stored by. */
export function chipKeyFor(
  kalshi: VenueBook | null | undefined,
  polymarket: VenueBook | null | undefined,
  fallback: string,
): string {
  return kalshi?.marketTicker ?? polymarket?.marketTicker ?? fallback;
}

/** A section bet row, reduced to what ladder-collapsing reads. The section's
 *  `visibleRows` are a structural superset of this. */
export interface LadderSourceRow {
  /** Passes the 0-vol / settled filters — hidden rows are skipped entirely. */
  visible: boolean;
  settled: boolean;
  synth: GameRow;
  /** Single-outcome row (a prop rung, a team-total rung, YRFI, extras).
   *  Undefined → an away/home pair, which is never a ladder. */
  singleSide?: TeamSide;
  /** Set on value-ladder rungs — the collapse key. `groupKey` is the ladder's
   *  stable identity WITHOUT the threshold; `groupLabel` is the row title
   *  without the number; `rungLabel` is the chip text ("2+"). */
  ladder?: { groupKey: string; groupLabel: string; rungLabel: string };
}

/** Where a row routes its trade / expand / positions — the selected rung's
 *  synth for a ladder, the row's own synth otherwise. */
export interface LadderTarget {
  synth: GameRow;
  teamSide: TeamSide;
}

/** One rendered Compact table row. */
export interface LadderTableRow {
  /** React key AND expand key. For a ladder this is the GROUP key, stable as
   *  the selection moves: keying on the selected rung would remount the row and
   *  slam the open orderbook shut on every chevron press. */
  key: string;
  /** What the table draws — the selected rung's markets under the ladder's
   *  threshold-free label, keyed by group. Trades must still route with
   *  `target.synth` so the ticket names the actual threshold market. */
  displaySynth: GameRow;
  target: LadderTarget;
  settled: boolean;
  /** Present only on a collapsed ladder with ≥2 rungs — the strip's chips,
   *  ascending. A lone rung renders as an ordinary row. */
  chips?: LineChipItem[];
  /** Index into `chips` currently shown. */
  selectedIdx?: number;
}

export interface LadderRowsResult {
  rows: LadderTableRow[];
  /** Ladder groupKey → (rung chip-id → target), so the caller can move an open
   *  ticket to whichever rung the user picks, by that rung's stable id. */
  rungTargets: Map<string, Map<string, LadderTarget>>;
}

/**
 * Build the Compact table rows from the section's rows.
 *
 * `rungKeyByGroup` is the caller's sticky per-ladder selection (groupKey → the
 * selected rung's chip id, i.e. its market ticker). A group that's absent — or
 * whose stored rung is no longer listed — falls back to the closest-to-50¢
 * rung, the same default the spread/total strips use; that lands on the line a
 * book would actually post (over 1.5 hits ≈ the 2+ rung) rather than the
 * always-lopsided first rung.
 */
export function buildLadderTableRows(
  rows: LadderSourceRow[],
  rungKeyByGroup: Map<string, string>,
): LadderRowsResult {
  const out: LadderTableRow[] = [];
  // groupKey → the row being filled + the rungs gathered so far, so the row
  // lands at the position of the group's FIRST visible rung (render order
  // preserved) and later rungs of the same ladder append to it.
  const ladders = new Map<
    string,
    {
      outIdx: number;
      groupLabel: string;
      rungs: Array<{
        synth: GameRow;
        teamSide: TeamSide;
        rungKey: string;
        rungLabel: string;
        fullLabel: string;
        kalshi: VenueBook | null;
        polymarket: VenueBook | null;
        settled: boolean;
      }>;
    }
  >();

  for (const r of rows) {
    if (!r.visible) continue;
    const s = r.synth;
    const teamSide: TeamSide = r.singleSide ?? 'away';
    const kalshi = (teamSide === 'away' ? s.kalshi?.away : s.kalshi?.home) ?? null;
    const polymarket = (teamSide === 'away' ? s.polymarket?.away : s.polymarket?.home) ?? null;

    if (!r.ladder) {
      // Not a ladder (YRFI, extra innings): an ordinary row, uncollapsed.
      out.push({
        key: s.key,
        displaySynth: s,
        target: { synth: s, teamSide },
        settled: r.settled,
      });
      continue;
    }
    // No book on either venue isn't a market — skip rather than print an empty
    // rung, so "unavailable" stays truthful.
    if (!kalshi && !polymarket) continue;
    const rung = {
      synth: s,
      teamSide,
      rungKey: `${s.key}|${teamSide}`,
      rungLabel: r.ladder.rungLabel,
      fullLabel: teamSide === 'away' ? s.awayName : s.homeName,
      kalshi,
      polymarket,
      settled: r.settled,
    };
    const existing = ladders.get(r.ladder.groupKey);
    if (existing) {
      existing.rungs.push(rung);
    } else {
      const outIdx = out.length;
      // Placeholder, keyed on the ladder's stable identity (no threshold).
      // Books / label / settled are resolved from the SELECTED rung below.
      out.push({
        key: r.ladder.groupKey,
        displaySynth: s,
        target: { synth: s, teamSide },
        settled: r.settled,
      });
      ladders.set(r.ladder.groupKey, {
        outIdx,
        groupLabel: r.ladder.groupLabel,
        rungs: [rung],
      });
    }
  }

  // Finalize each ladder: choose the shown rung — a sticky selection resolved
  // BY IDENTITY (the stored chip id, via findIndex), else the closest-to-50¢
  // default. Resolving by identity rather than array position is what avoids
  // the raw-index trap: the visible set shrinks as rungs settle / get filtered,
  // but a stored id that's no longer listed just falls back to the default
  // instead of pointing past the end.
  const rungTargets = new Map<string, Map<string, LadderTarget>>();
  for (const [groupKey, g] of ladders) {
    const row = out[g.outIdx];
    if (g.rungs.length === 1) {
      // A lone rung isn't a ladder — keep its full "N+" label, no strip.
      const only = g.rungs[0];
      row.displaySynth = only.synth;
      row.target = { synth: only.synth, teamSide: only.teamSide };
      row.settled = only.settled;
      continue;
    }
    const chips = g.rungs.map((x) => ({
      key: chipKeyFor(x.kalshi, x.polymarket, x.rungKey),
      rung: x,
    }));
    const storedKey = rungKeyByGroup.get(groupKey);
    const storedIdx = storedKey != null ? chips.findIndex((c) => c.key === storedKey) : -1;
    // Fall back to the nearest-to-50¢ Kalshi QUOTE (book.priceCents — the same
    // field linePriceCents feeds the spread/total default; a last-trade read
    // would skip never-traded rungs).
    const sel =
      storedIdx >= 0
        ? storedIdx
        : closestKalshiYesTo50(g.rungs.map((x) => x.kalshi?.priceCents ?? null));
    const chosen = g.rungs[sel];
    row.displaySynth = {
      ...chosen.synth,
      // The LADDER's key: stable across selection changes (see LadderTableRow).
      key: groupKey,
      // Threshold moves out of the name and into the strip — the point of it.
      awayName: g.groupLabel,
      homeName: g.groupLabel,
    };
    row.target = { synth: chosen.synth, teamSide: chosen.teamSide };
    row.settled = chosen.settled;
    row.chips = chips.map((c) => ({
      key: c.key,
      label: c.rung.rungLabel,
      priceCents: c.rung.kalshi?.priceCents ?? c.rung.polymarket?.priceCents ?? null,
    }));
    row.selectedIdx = sel;
    // Every rung's synth, keyed by its chip id, so the select handler can move
    // the ticket to whichever rung the user picks — robust to reordering.
    const byKey = new Map<string, LadderTarget>();
    for (const c of chips) byKey.set(c.key, { synth: c.rung.synth, teamSide: c.rung.teamSide });
    rungTargets.set(groupKey, byKey);
  }

  return { rows: out, rungTargets };
}
